import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Coffee, Croissant, Salad, MapPin, Star, ArrowUpRight } from "lucide-react";
import { WHY, STATS, IMG } from "../data/content";
import { SectionLabel } from "./Section";
import { fadeUp, stagger, viewport } from "../lib/motion";

const ICONS = { Coffee, Croissant, Salad, MapPin, Star };

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState(0);
  const decimals = value % 1 !== 0 ? 1 : 0;

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {display.toFixed(decimals)}
      {suffix}
    </span>
  );
};

const WhyDetour = () => (
  <section id="why" className="relative py-16 sm:py-32" data-testid="why-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="max-w-2xl mb-14">
        <SectionLabel>Why Detour</SectionLabel>
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]"
        >
          More than a coffee shop — <span className="text-highlight">a neighborhood ritual.</span>
        </motion.h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 lg:gap-14 items-stretch">
        {/* Feature image */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="relative overflow-hidden rounded-[2rem] min-h-[440px] lg:min-h-full shadow-warm-lg group"
          data-testid="why-feature-image"
        >
          <img
            src={IMG.mugChandelier}
            alt="The signature mug chandelier inside Detour Coffee Co."
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-black/25" />

          {/* floating rating chip */}
          <div className="absolute top-6 left-6 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 px-4 py-2">
            <Star size={14} className="fill-highlight text-highlight" />
            <span className="text-background text-xs tracking-wide">Community Favorite · 4.5★</span>
          </div>

          <div className="absolute bottom-0 p-8">
            <p className="text-highlight text-[11px] uppercase tracking-[0.24em] font-semibold mb-2">Est. 2017 · Arlington, VA</p>
            <h3 className="text-background text-2xl sm:text-3xl leading-tight max-w-sm">
              Roasted, baked and served with intention — every single day.
            </h3>
          </div>
        </motion.div>

        {/* Reasons list */}
        <motion.ul
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="flex flex-col"
        >
          {WHY.map((w, i) => {
            const Icon = ICONS[w.icon];
            return (
              <motion.li
                key={w.title}
                variants={fadeUp}
                className="group flex items-start gap-5 py-6 border-b border-surface first:pt-0 transition-colors"
                data-testid={`why-item-${i}`}
              >
                <span className="font-serif text-lg text-highlight/70 pt-1 w-8 shrink-0 tabular-nums">
                  0{i + 1}
                </span>
                <span className="h-12 w-12 shrink-0 rounded-2xl bg-surface text-primary flex items-center justify-center transition-colors duration-300 group-hover:bg-primary group-hover:text-background">
                  <Icon size={22} strokeWidth={1.6} />
                </span>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-xl text-foreground">{w.title}</h3>
                    <ArrowUpRight size={18} className="text-foreground/25 transition-all duration-300 group-hover:text-highlight group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                  <p className="mt-1.5 text-sm text-foreground/65 leading-relaxed">{w.copy}</p>
                </div>
              </motion.li>
            );
          })}
        </motion.ul>
      </div>

      {/* Stats strip */}
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
        className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px overflow-hidden rounded-[2rem] bg-surface shadow-warm"
        data-testid="stats-strip"
      >
        {STATS.map((s) => (
          <motion.div key={s.label} variants={fadeUp} className="bg-background p-8 sm:p-10 text-center">
            <p className="font-serif text-4xl sm:text-5xl text-primary">
              <Counter value={s.value} suffix={s.suffix} />
            </p>
            <p className="mt-2 text-xs sm:text-sm uppercase tracking-wide text-foreground/60">{s.label}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

export default WhyDetour;
